import { useEvent } from '@shared-core/hooks/use-event'
import { getAdapter, navigation } from '@shared-core/navigation/navigation'
import type { NavKey, NavParams } from '@shared-core/navigation/routes'

// Кросс-платформенные навигационные хуки. core остаётся RN-free и делегирует
// платформенному адаптеру (mobile: react-navigation; web: router + sheets).
// Поэтому путь импорта навигации всегда @shared-core/navigation — и для mobile, и для web.

// Маршрут (route): поле params. Совместимо с useRoute().params.
export type RouteLike<P = unknown> = { key: string; name: string; params: P }

// Типизированные params экрана/шита (аналог RootRouteProps из mobile screens.ts).
export type RootRouteProps<RouteName extends NavKey> = RouteLike<NavParams[RouteName]>

// Состояние навигатора (для useNavigationState).
export type NavigationStateLike = {
  key: string
  index: number
  routeNames: string[]
  routes: Array<RouteLike & { state?: NavigationStateLike }>
  type: string
  stale: false
}

type NavigationRouteInput = {
  key: string
  name: string
  params?: unknown
  state?: NavigationStateInput
}

type NavigationStateInput = {
  key?: string
  index?: number
  routeNames?: string[]
  routes: NavigationRouteInput[]
  type?: string
  stale?: false
}

export function findFocusedRoute(state: unknown): RouteLike | undefined {
  if (!state || typeof state !== 'object') return undefined

  const navigationState = state as NavigationStateInput
  const route = navigationState.routes?.[navigationState.index ?? 0]

  if (!route?.name) return undefined

  if (route.state) {
    return findFocusedRoute(route.state)
  }

  return {
    key: route.key ?? route.name,
    name: route.name,
    params: route.params,
  } as RouteLike
}

// Дефолтный «широкий» тип навигации для вызовов useNavigation() без явного типа
// (когда тип не указан — как react-navigation по умолчанию).
export type NavigationLike = {
  navigate: (screen: NavKey, params?: unknown) => void
  goBack: () => void
  pop: (count?: number) => void
  popTo: (screen: NavKey, params?: unknown) => void
  popToTop: () => void
  push: (screen: NavKey, params?: unknown) => void
  replace: (screen: NavKey, params?: unknown) => void
  reset: (state: unknown) => void
  dispatch: (action: unknown) => void
  setParams: (params: object) => void
  setOptions: (options: object) => void
  canGoBack: () => boolean
  isFocused: () => boolean
  addListener: (type: string, callback: (...args: unknown[]) => void) => () => void
  removeListener: (type: string, callback: (...args: unknown[]) => void) => void
  getParent: () => NavigationLike | undefined
  getState: () => NavigationStateLike
  getCurrentRoute: () => RouteLike | undefined
  preload: (screen: NavKey, params?: unknown) => void
}

// react-navigation-совместимый useNavigation. Тип задаёт вызывающий код (как в RN).
export function useNavigation<T = NavigationLike>(): T {
  return getAdapter().useNavigation() as T
}

// react-navigation-совместимый useRoute: возвращает route (с полем params).
export function useRoute<T = RouteLike>(): T {
  return getAdapter().useRoute() as T
}

export function useIsFocused(): boolean {
  return getAdapter().useIsFocused()
}

export function useFocusEffect(effect: () => undefined | (() => void)): void {
  getAdapter().useFocusEffect(effect)
}

export function useNavigationState<T>(selector: (state: NavigationStateLike) => T): T {
  return getAdapter().useNavigationState(selector as (state: unknown) => T)
}

export function useCloseFormSheets(): () => void {
  return getAdapter().useCloseFormSheets()
}

// go/popTo/toGo строятся поверх кросс-платформенного фасада (RN-free).
// Сигнатуры повторяют исходный mobile use-nav: path — полный union NavKey,
// дженерик T только в params (params остаётся «широким», без новых ошибок типов).
const nav = navigation as {
  navigate: (key: NavKey, params?: unknown) => void
  navigateFromSheet: (key: NavKey, params?: unknown) => void
  navigateDismissingSheets: (key: NavKey, params?: unknown) => void
  popTo: (key: NavKey, params?: unknown) => void
  dismissSheetsBelowCurrent: () => void
}

export const useToGo = () =>
  useEvent(
    <T extends NavKey>(path: NavKey, params?: NavParams[T]) =>
      () =>
        nav.navigate(path, params),
  )

export const useGo = () =>
  useEvent(<T extends NavKey>(path: NavKey, params?: NavParams[T]) => {
    nav.navigate(path, params)
  })

// Переход на экран с закрытием текущего формшита-источника (напр. после security-check).
// Разница платформ инкапсулирована в адаптере (native: goBack+navigate, web: navigate).
export const useGoFromSheet = () =>
  useEvent(<T extends NavKey>(path: NavKey, params?: NavParams[T]) => {
    nav.navigateFromSheet(path, params)
  })

// Fullscreen-переход с закрытием всех modal/form-sheet оверлеев (antifraud из swap и т.п.).
export const useGoDismissingSheets = () =>
  useEvent(<T extends NavKey>(path: NavKey, params?: NavParams[T]) => {
    nav.navigateDismissingSheets(path, params)
  })

// Снять формшиты под текущим экраном перед уходом на обычный экран (см. dismissSheetsBelowCurrent).
export const useDismissSheetsBelow = () =>
  useEvent(() => {
    nav.dismissSheetsBelowCurrent()
  })

export const usePopTo = () =>
  useEvent(<T extends NavKey>(path: NavKey, params?: NavParams[T]) => {
    nav.popTo(path, params)
  })

export const useNav = () => {
  const toGo = useToGo()
  const go = useGo()
  const popTo = usePopTo()
  const closeFormSheets = useCloseFormSheets()

  return { toGo, go, popTo, closeFormSheets }
}
